import { m } from "@estate-platform/i18n/messages";
import { Skeleton } from "@estate-platform/ui/skeleton";
import { useEffect, useEffectEvent, useRef } from "react";

import type { ReactElement } from "react";

const rowKeys = ["first", "second", "third"] as const;

export interface DialogPendingProps {
  readonly title: string;
  readonly onClose: () => void;
}

// WHY: the fallback of a lazy dialog, so it opens at once with its frame while its code loads; it stays free of Base UI, which would otherwise join the shell's first load.
export const DialogPending = ({ title, onClose }: DialogPendingProps): ReactElement => {
  const frameRef = useRef<HTMLDivElement>(null);
  const close = useEffectEvent(onClose);
  useEffect(() => {
    frameRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim expanded:items-center">
      <div
        ref={frameRef}
        role="dialog"
        aria-modal
        aria-busy
        aria-label={title}
        tabIndex={-1}
        className="flex max-h-[92%] w-full max-w-medium flex-col gap-5 rounded-t-sheet bg-surface p-6 shadow-float outline-none expanded:w-[min(640px,calc(100%-32px))] expanded:rounded-sheet"
      >
        <p className="font-display text-display-m text-ink">{title}</p>
        <div role="status" aria-label={m.loading()} className="flex flex-col gap-4">
          {rowKeys.map((key) => (
            <Skeleton key={key} shape="block" width="100%" height="2.75rem" />
          ))}
        </div>
      </div>
    </div>
  );
};
